/*
 * Purpose: Builder layout — resizable canvas, config, results, map AI assistant columns
 * Last modified: 2026-07-10 | Agent
 * Completeness: 98
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { Sparkles } from 'lucide-react';
import { Canvas } from '@/components/Canvas';
import { MapAiAssistant } from '@/components/MapAiAssistant';
import { MissionLiveBuildController } from '@/components/mission/MissionLiveBuildController';
import { MissionMapGoalBanner } from '@/components/mission/MissionMapGoalBanner';
import { MissionMapResultsBar } from '@/components/mission/MissionMapResultsBar';
import { WorkflowPanel } from '@/components/WorkflowPanel';
import { FlowSummaryPanel } from '@/components/FlowSummaryPanel';
import { ExecutionResults } from '@/components/ExecutionResults';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useExecutionRuntimeStore } from '@/stores/executionRuntimeStore';
import { useExecutionTraceStore } from '@/stores/executionTraceStore';
import type { ExecutionContext } from '@/services/executor';
import { toast } from 'sonner';
import { toastContextualIssue } from '@/lib/contextualIssuePolicy';
import {
  openConnectorFixForWorkflow,
  openConnectorFixFromError,
} from '@/stores/connectorFixStore';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { WorkflowValidator } from '@/services/validation';
import { useMissionWorkspace } from '@/contexts/MissionWorkspaceContext';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';
import { assertCanRunFlows } from '@/lib/subscriptionEnforce';
import { APP_ROUTES } from '@/lib/appRoutes';
import { Link } from 'react-router-dom';
import { getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';
import { SmartBlockInputDialog } from '@/components/mission/SmartBlockInputDialog';

interface BuilderWorkspaceProps {
  /** When true, register flow-scoped keyboard shortcuts (save / run). */
  shortcutsActive?: boolean;
}

const CONFIG_PANEL_SIZE = 26;
const RESULTS_PANEL_SIZE = 20;
const ASSISTANT_PANEL_SIZE = 28;

export function BuilderWorkspace({ shortcutsActive = true }: BuilderWorkspaceProps) {
  const isMobile = useIsMobile();
  const { setRuntime } = useMissionWorkspace();
  const { canUse } = useSubscriptionGate();
  const canUseMapAssistant = canUse('map_ai_assistant');
  const { selectedNode, selectNode, currentWorkflow, saveWorkflow } = useWorkflowStore();
  const configPanelRef = useRef<ImperativePanelHandle>(null);
  const resultsPanelRef = useRef<ImperativePanelHandle>(null);
  const assistantPanelRef = useRef<ImperativePanelHandle>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState<ExecutionContext[]>([]);
  const [assistantSheetOpen, setAssistantSheetOpen] = useState(false);
  const [assistantCollapsed, setAssistantCollapsed] = useState(false);
  const [smartInputDialogOpen, setSmartInputDialogOpen] = useState(false);
  const hasResults = executionResults.length > 0;
  const showFlowPanel = Boolean(currentWorkflow);
  const isNodeConfigOpen = Boolean(selectedNode);
  const selectedNodeObj = useMemo(
    () => (selectedNode ? currentWorkflow?.nodes.find((n) => n.id === selectedNode) ?? null : null),
    [currentWorkflow, selectedNode]
  );
  const selectedNodeSmartNeeds = useMemo(
    () => (selectedNodeObj ? getSmartBlockInputRequirements(selectedNodeObj) : []),
    [selectedNodeObj]
  );

  const abortRef = useRef<AbortController | null>(null);

  const collapseAssistant = useCallback(() => {
    assistantPanelRef.current?.collapse();
    setAssistantCollapsed(true);
  }, []);

  const expandAssistant = useCallback(() => {
    if (!canUseMapAssistant) {
      toast.error('Map AI Assistant requires Plus plan.', {
        action: { label: 'View plans', onClick: () => { window.location.href = APP_ROUTES.pricing; } },
      });
      return;
    }
    if (isMobile) {
      setAssistantSheetOpen(true);
      return;
    }
    const panel = assistantPanelRef.current;
    if (!panel) return;
    panel.expand();
    panel.resize(ASSISTANT_PANEL_SIZE);
    setAssistantCollapsed(false);
  }, [isMobile, canUseMapAssistant]);

  const handleExecute = useCallback(async () => {
    if (!assertCanRunFlows()) return;

    if (!currentWorkflow) {
      toastContextualIssue('No flow selected', {
        label: 'Open flows',
        href: REMEDIATION.flows,
      });
      return;
    }

    const errors = WorkflowValidator.validate(currentWorkflow).filter((e) => e.type === 'error');
    if (errors.length > 0) {
      toastContextualIssue('Fix check issues before starting', {
        label: 'Open check',
        onClick: () =>
          document.querySelector<HTMLButtonElement>('[data-flow-check-trigger]')?.click(),
      });
      return;
    }

    if (
      openConnectorFixForWorkflow(currentWorkflow, {
        onRetry: () => void handleExecute(),
      })
    ) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    useExecutionRuntimeStore
      .getState()
      .registerRun(currentWorkflow.id, currentWorkflow.name, controller);
    useExecutionTraceStore.getState().startTrace(currentWorkflow.id);

    setIsExecuting(true);
    setExecutionResults([]);
    try {
      const results = await useWorkflowStore.getState().executeWorkflow(controller.signal, {
        onNodeStart: (nodeId) => useExecutionTraceStore.getState().markNodeStart(currentWorkflow.id, nodeId),
        onNodeComplete: (nodeId) =>
          useExecutionTraceStore.getState().markNodeComplete(currentWorkflow.id, nodeId),
        onNodeError: (nodeId, message) =>
          useExecutionTraceStore.getState().markNodeFailed(currentWorkflow.id, nodeId, message),
      });
      if (!controller.signal.aborted) {
        setExecutionResults(results);
        toast.success('Flow started and completed');
        if (!isMobile) {
          resultsPanelRef.current?.expand();
          resultsPanelRef.current?.resize(RESULTS_PANEL_SIZE);
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        useExecutionTraceStore.getState().finishTrace(currentWorkflow.id);
        toast.message('Flow stopped');
        return;
      }
      const msg = error instanceof Error ? error.message : 'Execution failed';
      if (
        !openConnectorFixFromError(error, {
          workflow: currentWorkflow,
          onRetry: () => void handleExecute(),
        })
      ) {
        toastContextualIssue(msg, {
          label: 'Retry run',
          onClick: () => void handleExecute(),
        });
      }
    } finally {
      useExecutionRuntimeStore.getState().unregisterRun(currentWorkflow.id);
      useExecutionTraceStore.getState().finishTrace(currentWorkflow.id);
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setIsExecuting(false);
    }
  }, [currentWorkflow, isMobile]);

  const handleStop = useCallback(() => {
    if (currentWorkflow) {
      useExecutionRuntimeStore.getState().stopRun(currentWorkflow.id);
    } else {
      abortRef.current?.abort();
    }
    setIsExecuting(false);
    toast.message('Stopping flow…');
  }, [currentWorkflow]);

  useEffect(() => {
    setRuntime({
      isExecuting,
      onExecute: handleExecute,
      onStop: handleStop,
      onOpenAssistant: expandAssistant,
    });
    return () => setRuntime(null);
  }, [setRuntime, isExecuting, handleExecute, handleStop, expandAssistant]);

  useEffect(() => {
    if (!shortcutsActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveWorkflow();
        toast.success('Flow saved');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void handleExecute();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleExecute, saveWorkflow, shortcutsActive]);

  useEffect(() => {
    if (isMobile) return;
    const panel = configPanelRef.current;
    if (!panel) return;

    if (currentWorkflow) {
      panel.expand();
      panel.resize(CONFIG_PANEL_SIZE);
    } else {
      panel.collapse();
    }
  }, [currentWorkflow, isMobile]);

  useEffect(() => {
    if (isMobile || !hasResults) return;
    resultsPanelRef.current?.expand();
  }, [hasResults, isMobile]);

  useEffect(() => {
    if (!selectedNodeObj || selectedNodeSmartNeeds.length === 0) {
      setSmartInputDialogOpen(false);
    }
  }, [selectedNodeObj, selectedNodeSmartNeeds.length]);

  const handleCloseConfig = () => selectNode(null);

  const collapseResults = useCallback(() => {
    resultsPanelRef.current?.collapse();
  }, []);

  const expandResults = useCallback(() => {
    if (isMobile) {
      document.getElementById('mission-mobile-results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
    resultsPanelRef.current?.expand();
    resultsPanelRef.current?.resize(RESULTS_PANEL_SIZE);
  }, [isMobile]);

  const mapChrome = (
    <>
      <MissionLiveBuildController />
      <MissionMapGoalBanner />
      <Canvas />
      <MissionMapResultsBar
        results={executionResults}
        isExecuting={isExecuting}
        workflow={currentWorkflow}
        onExpandDetails={hasResults ? expandResults : undefined}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="flex flex-1 flex-col min-h-0 h-full w-full gap-0 rounded-lg border border-border overflow-hidden">
        <div className="flex-1 min-h-0 overflow-hidden p-2 flex flex-col">
          <div className="ab-map-well relative flex-1 min-h-0">
            {mapChrome}
          </div>
        </div>
        <Sheet open={isNodeConfigOpen} onOpenChange={(open) => !open && handleCloseConfig()}>
          <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
            <WorkflowPanel
              onClose={handleCloseConfig}
              onOpenSmartInput={() => setSmartInputDialogOpen(true)}
            />
          </SheetContent>
        </Sheet>
        {!selectedNode && currentWorkflow && (
          <div className="mx-2 mb-2 rounded-lg border border-border bg-card overflow-hidden max-h-[min(42vh,360px)] shrink-0">
            <FlowSummaryPanel className="border-l-0 h-full" />
          </div>
        )}
        <Sheet open={assistantSheetOpen} onOpenChange={setAssistantSheetOpen}>
          <SheetContent side="right" className="flex h-full max-h-[100dvh] w-full flex-col overflow-hidden p-0 sm:max-w-md">
            {canUseMapAssistant ? (
              <MapAiAssistant layout="column" className="h-full min-h-0" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center gap-3">
                <Sparkles className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Map AI Assistant is available on Plus.</p>
                <Button actionTier="secondary" size="sm" asChild>
                  <Link to={APP_ROUTES.pricing}>Upgrade to Plus</Link>
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
        {hasResults && (
          <div id="mission-mobile-results" className="mt-3 rounded-lg border border-border bg-card overflow-hidden max-h-48">
            <div className="p-3 border-b border-border">
              <h3 className="font-semibold text-sm text-foreground">Results</h3>
            </div>
            <div className="p-3 overflow-auto">
              <ExecutionResults results={executionResults} workflow={currentWorkflow} />
            </div>
          </div>
        )}
        <SmartBlockInputDialog
          node={selectedNodeObj}
          open={smartInputDialogOpen}
          onOpenChange={setSmartInputDialogOpen}
        />
      </div>
    );
  }

  const sidePanels =
    (showFlowPanel ? CONFIG_PANEL_SIZE : 0) +
    (hasResults ? RESULTS_PANEL_SIZE : 0) +
    ASSISTANT_PANEL_SIZE;
  const canvasDefaultSize = Math.max(35, 100 - sidePanels);

  return (
    <div className="flex flex-1 flex-col min-h-0 h-full w-full rounded-lg border border-border overflow-hidden">
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-[240px] w-full">
        {/* Map canvas — left */}
        <ResizablePanel
          defaultSize={canvasDefaultSize}
          minSize={28}
          order={1}
          className="min-h-0 flex flex-col"
        >
          <div className="ab-map-well flex-1 min-h-0 relative w-full h-full">
            {mapChrome}
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle className="bg-border" />

        {/* Flow summary / block config — open when a flow is selected */}
        <ResizablePanel
          ref={configPanelRef}
          order={2}
          collapsible
          collapsedSize={0}
          defaultSize={showFlowPanel ? CONFIG_PANEL_SIZE : 0}
          minSize={16}
          maxSize={38}
          className="bg-card min-h-0 h-full flex flex-col"
        >
          {currentWorkflow ? (
            <WorkflowPanel
              onClose={handleCloseConfig}
              onOpenSmartInput={() => setSmartInputDialogOpen(true)}
            />
          ) : (
            <div className="h-full w-full" aria-hidden />
          )}
        </ResizablePanel>

        <ResizableHandle withHandle className="bg-border" />

        {/* Execution results — collapsible */}
        <ResizablePanel
          ref={resultsPanelRef}
          order={3}
          collapsible
          collapsedSize={0}
          defaultSize={hasResults ? RESULTS_PANEL_SIZE : 0}
          minSize={14}
          maxSize={32}
          className="bg-card min-h-0"
        >
          {hasResults ? (
            <div className="h-full flex flex-col overflow-hidden min-h-0">
              <div className="p-3 border-b border-border shrink-0 flex items-center justify-between gap-2">
                <h3 className="font-semibold text-sm text-foreground">Results</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={collapseResults}
                >
                  Collapse
                </Button>
              </div>
              <div className="flex-1 p-3 overflow-auto min-h-0">
                <ExecutionResults results={executionResults} workflow={currentWorkflow} />
              </div>
            </div>
          ) : (
            <div className="h-full w-full" aria-hidden />
          )}
        </ResizablePanel>

        <ResizableHandle withHandle className="bg-border" />

        {/* Map AI Assistant — far right, collapsible + resizable */}
        <ResizablePanel
          ref={assistantPanelRef}
          order={4}
          collapsible
          collapsedSize={0}
          defaultSize={ASSISTANT_PANEL_SIZE}
          minSize={18}
          maxSize={42}
          className="min-h-0 h-full max-h-full flex flex-col overflow-hidden"
          onCollapse={() => setAssistantCollapsed(true)}
          onExpand={() => setAssistantCollapsed(false)}
        >
          {canUseMapAssistant ? (
            <MapAiAssistant layout="column" className="h-full min-h-0" onRequestCollapse={collapseAssistant} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-4 text-center gap-2">
              <Sparkles className="h-6 w-6 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Plus plan required</p>
              <Button actionTier="ghost" size="sm" className="h-7 text-xs" asChild>
                <Link to={APP_ROUTES.pricing}>Upgrade</Link>
              </Button>
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>

      {assistantCollapsed && canUseMapAssistant && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="fixed right-4 top-[var(--app-top-offset,3.5rem)] z-40 flex items-center gap-1.5 rounded-full border border-border shadow-md px-3 py-1.5"
          onClick={expandAssistant}
          aria-label="Open Map AI Assistant"
        >
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-xs font-medium">AI Assistant</span>
        </Button>
      )}
      <SmartBlockInputDialog
        node={selectedNodeObj}
        open={smartInputDialogOpen}
        onOpenChange={setSmartInputDialogOpen}
      />
    </div>
  );
}
